import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DataService } from 'src/app/_helpers';
import { ConfigurationService } from 'src/app/_services';
import { bluePlusIcon, mailIcon } from 'src/app/sharedcomp/constans/icons.constans';

const DAY_MAP: { [key: string]: string } = {
  Monday: 'Lunes', Tuesday: 'Martes', Wednesday: 'Miércoles',
  Thursday: 'Jueves', Friday: 'Viernes', Saturday: 'Sábado', Sunday: 'Domingo'
};

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

@Component({
  selector: 'app-create-notification-form',
  templateUrl: './create-notification-form.component.html',
  styleUrls: [
    './create-notification-form.component.css',
    '../../../../../../../assets/css/new-component-view.css',
    '../../../../../../../assets/css/new-buttons-theme.css',
    '../../../../../../../assets/css/new-form-elements.css'
  ]
})
export class CreateNotificationFormComponent implements OnInit, OnChanges {

  @Output() cancelCreation: EventEmitter<boolean> = new EventEmitter();
  @Output() getNotificationList: EventEmitter<boolean> = new EventEmitter();
  @Input() customerId: string;
  @Input() custNotifyTemplateList: any[] = [];

  icons: Record<string, { path: string; alt: string }>;

  createNotificationForm: FormGroup;
  templateListLimit: number = 100;
  isLimit: boolean = false;
  restrictionMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private configurationService: ConfigurationService,
    private dataService: DataService
  ) {
    this.icons = this.setIcons();
    this.initNewNotificationForm();
  }

  ngOnInit() {
    this.loadRestrictionMessage();
  }

  private buildRestrictionMessage(config: any): void {
    try {
      const days: string[] = config.days_allowed || [];
      const timeRange = config.time_range || {};

      const dayNames = days.map((d: string) => DAY_MAP[d] || d);
      const daysLabel = dayNames.length > 1
        ? dayNames.slice(0, -1).join(', ') + ' y ' + dayNames[dayNames.length - 1]
        : dayNames[0] || '';

      const fromTime = timeRange.from ? formatTime(timeRange.from) : '';
      const toTime   = timeRange.to   ? formatTime(timeRange.to)   : '';

      this.restrictionMessage =
        'Ley 29571: Prohibido el envío fuera del horario permitido ' +
        '(' + daysLabel + ' de ' + fromTime + ' a ' + toTime + '). ' +
        'No permitido en días festivos; ' +
        'si cae en estos días, se enviará el siguiente día hábil.';
    } catch (e) {
      this.restrictionMessage = null;
    }
  }

  private get globalRestrictionConfig$() {
    return this.configurationService.getNotificationRestrictionConfig().pipe(
      map(r => (r && r.status === 1 && r.metadata && r.metadata.config && r.metadata.config.valuesDefault) || null),
      catchError(() => of(null))
    );
  }

  loadRestrictionMessage() {
    this.configurationService.getNotificationRestrictionConfigByCustomer(this.customerId)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(response => {
          const customerConfig = response && response.values && response.values.days_allowed && response.values;
          const isCustomerActive = response && response.status === 'ACTIVE';
          return isCustomerActive && customerConfig ? of(customerConfig) : this.globalRestrictionConfig$;
        }),
        catchError(() => this.globalRestrictionConfig$)
      )
      .subscribe(config => {
        config ? this.buildRestrictionMessage(config) : this.restrictionMessage = null;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customerId) {
      const customerId = changes.customerId.currentValue;
      this.createNotificationForm.get('customer.public_id').setValue(customerId);
      if (!changes.customerId.firstChange) {
        this.loadRestrictionMessage();
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setNotificationStatus(event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.createNotificationForm.get('status').setValue(checked ? 'ACTIVE' : 'INACTIVE');
  }

  observerCreationForm() {
    this.createNotificationForm.get('values.days').valueChanges
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe(day => {
      if (day >= 180) {
        this.isLimit = true;
        this.createNotificationForm.get('values.hours').setValue(0);
        this.createNotificationForm.get('values.minutes').setValue(0);
      } else {
        this.isLimit = false;
      }
    });
  }

  increment(controlName: string) {
    const control = this.createNotificationForm.get(`values.${controlName}`);
    let value = control.value;

    if (controlName === 'days' && value < 180) {
      value++;
    } else if (controlName === 'hours' && value < 23 && !this.isLimit) {
      value++;
    } else if (controlName === 'minutes' && value < 59 && !this.isLimit) {
      value++;
    }

    control.setValue(value);
  }

  decrement(controlName: string) {
    const control = this.createNotificationForm.get(`values.${controlName}`);
    let value = control.value;

    if (value > 0) {
      value--;
    }

    control.setValue(value);
  }

  changeTemplate(event) { }

  initNewNotificationForm() {
    this.createNotificationForm = this.formBuilder.group({
      name: new FormControl("", Validators.required),
      description: new FormControl("", Validators.required),
      status: new FormControl("ACTIVE"),
      object_related: new FormControl("INVOICE"),
      type: new FormControl("NOTIFICATION"),
      customer: this.formBuilder.group({
        public_id: new FormControl("")
      }),
      values: this.formBuilder.group({
        days: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(180)]),
        hours: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(23)]),
        minutes: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(59)]),
        selector: new FormControl("1"),
        template_id: new FormControl("", Validators.required)
      })
    });
    this.observerCreationForm();
  }

  setIcons() {
    return {
      bluePlus: bluePlusIcon,
      mail: mailIcon
    };
  }

  closeCreateUserSidebar() {
    this.cancelCreation.emit(false);
  }

  createNotification() {
    this.dataService.loadSpinner(true);
    console.log(this.createNotificationForm.value)
    const formValue = this.createNotificationForm.value;
    this.configurationService.createConfiguration(formValue)
    .pipe(
      takeUntil(this.destroy$),
      tap(data => {
        this.getNotificationList.emit(true);
        this.dataService.loadSpinner(false);
        this.closeCreateUserSidebar();
      }),
      catchError(error => {
        console.error('Failed while trying to save the Notification Template Details.' + error);
        this.dataService.loadSpinner(false);
        this.closeCreateUserSidebar();
        return of(null);
      })
    ).subscribe();
  }

}
